import * as React from 'react';
import { useItemExistsInWishList, removeWishListItem, addWishListItem } from '@avensia/excite-commercetools';
import { styled } from '@glitz/react';
import { type PropType as ButtonPropType, FeedbackStandaloneButton } from 'webapp/components/button';
import { Theme } from 'webapp/theme';
import { translate } from '@avensia/excite';
import { HeartFilledIcon, HeartIcon } from 'webapp/components/icons';
import { a11yGrey } from 'webapp/shared/sharedTheme';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';
import { ariaLiveObservable } from 'webapp/shared/aria-live-region';
import { useWishListCount } from '@avensia/excite-commercetools/wishlist';
import { FieldSize } from './field-size';

type PropType = ButtonPropType & { product: NordicNest.Api.PartialProductApiModel; location: string; name: string };
export const WishlistButton = styled(({ product, location, name, ...restProps }: PropType) => {
  const request: Excite.Commercetools.WishlistRequest = {
    sku: product.sku,
    packageKey: undefined,
    packagePartKey: undefined,
  };
  const existInWishList = useItemExistsInWishList(request);
  const count = useWishListCount();

  const toggleWishlistItem = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    try {
      if (existInWishList) {
        await removeWishListItem(request, { ecommerce: { button_location: location, button_name: name } });
        ariaLiveObservable.emit({
          message: translate(t => t.wishlist.totalItems, { count: count - 1 }),
        });
      } else {
        const response = await addWishListItem(request, {
          ecommerce: { button_location: location, button_name: name },
        });

        if (response.success) {
          ariaLiveObservable.emit({
            message: translate(t => t.wishlist.totalItems, { count: count + 1 }),
          });
        } else {
          throw new Error('Failed to add to wishlist');
        }
      }
    } catch (error) {
      console.error('Wishlist operation failed:', error);
      ariaLiveObservable.emit({
        message: translate(t => t.wishlist.addToWishlistError, { count: count }),
      });
    }
  };

  const FavoriteIcon = existInWishList ? HeartFilledIcon : HeartIcon;
  return product.isPackage || product.isBundle ? null : (
    <FeedbackStandaloneButton
      {...restProps}
      css={buttonHoverDecorator}
      disableRippleEffect
      onClick={toggleWishlistItem}
      as={Theme.Transparent}
      aria-label={translate(t => t.wishlist.addToWishlist, { product: product.displayName })}
      aria-pressed={existInWishList}
      type="button"
      size={FieldSize.XXSmall}
    >
      <FavoriteIcon css={styled(existInWishList && likedDecorator, iconDecorator)} />
    </FeedbackStandaloneButton>
  );
});

const iconDecorator = styled({
  fontSize: t => t.sharedFontSize.s,
});

const buttonHoverDecorator = styled(createButtonHoverDecorator({ color: a11yGrey }), {
  color: t => t.white.text,
});

const likedDecorator = styled({
  color: t => t.productCard?.wishlistIcon?.color,
  fill: t => t.productCard?.wishlistIcon?.color,
  ':hover': {
    color: a11yGrey,
    fill: a11yGrey,
  },
});
